<template>
  <v-skeleton-loader
    :loading="loading"
    type="heading, table-thead, table-row-divider@8"
  >
    <v-card>
      <v-card-title class="py-0">
        <h2 class="text-h2 mt-10 mb-7">
          {{ $t('rateConfigurationStrings.diet.header') }}
        </h2>
      </v-card-title>
      <v-card-text>
        <v-row>
          <v-col
            cols="12"
            sm="6"
            lg="4"
            xl="3"
          >
            <vee-field
              v-slot="{ componentField, errors }"
              v-model="dietRates.accountingId"
              rules="finance_account"
              :name="$t('rateConfigurationStrings.diet.accountingLabel')"
              :skip-if-empty="false"
              vid="diet.accountingId"
            >
              <v-text-field
                v-bind="componentField"
                variant="outlined"
                density="compact"
                :error-messages="errors"
                :label="$t('rateConfigurationStrings.diet.accountingLabel')"
              />
            </vee-field>
          </v-col>
          <v-col
            cols="12"
            sm="6"
            lg="4"
            xl="3"
          >
            <vee-field
              v-slot="{ componentField, errors }"
              v-model="dietRates.nightAdditionAccountingId"
              rules="finance_account"
              :name="$t('rateConfigurationStrings.diet.nightAdditionAccountingLabel')"
              :skip-if-empty="false"
              vid="diet.nightAdditionAccountingId"
            >
              <v-text-field
                v-bind="componentField"
                variant="outlined"
                density="compact"
                :error-messages="errors"
                :label="$t('rateConfigurationStrings.diet.nightAdditionAccountingLabel')"
              />
            </vee-field>
          </v-col>
        </v-row>
        <v-row>
          <v-col
            cols="12"
            lg="8"
            xl="6"
          >
            <v-card-subtitle>
              <h3 class="text-h3">
                {{ $t("rateConfigurationStrings.diet.lengthSubheader") }}
              </h3>
            </v-card-subtitle>
            <v-table>
              <template #default>
                <thead>
                  <tr>
                    <th
                      id="dietLengthEnabledHeader"
                      class="text-left"
                    >
                      {{ $t('rateConfigurationStrings.diet.lengthEnabledHeader') }}
                    </th>
                    <th
                      id="dietLengthRateHeader"
                      class="text-left"
                    >
                      {{ $t('rateConfigurationStrings.diet.lengthRateHeader') }}
                    </th>
                    <th
                      id="dietLengthAmountHeader"
                      class="text-left"
                    >
                      {{ $t('rateConfigurationStrings.diet.lengthAmountHeader') }}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="item in dietRates.dietLengthRateTypes"
                    :key="item.rateType"
                  >
                    <td>
                      <vee-field
                        v-slot="{ componentField }"
                        v-model="item.enabled"
                        rules
                        :vid="getDietLengthEnabledValidationId(item.rateType)"
                      >
                        <v-checkbox
                          v-bind="componentField"
                          color="primary"
                          :aria-label="$t('commonStrings.tableAriaLabel',[$t('rateConfigurationStrings.diet.lengthEnabledHeader'), $t(`enums.dietLengthTypes.${item.rateType}`)])"
                        />
                      </vee-field>
                    </td>
                    <td>{{ $t(`enums.dietLengthTypes.${item.rateType}`) }}</td>
                    <td>
                      <vee-field
                        v-slot="{ componentField, errors }"
                        v-model="item.rate"
                        :rules="{required_if:getDietLengthEnabledValidationId(item.rateType), integer: true, min_value: item.enabled ? 1 : 0}"
                        :name="$t('rateConfigurationStrings.diet.lengthAmountHeader')"
                      >
                        <integer-text-field
                          v-bind="componentField"
                          name="rate"
                          :messages="verifyLengthTypeTaxRatesWarnings(item)"
                          :error-messages="errors"
                          :disabled="!item.enabled"
                          :aria-label="$t('commonStrings.tableAriaLabel',[$t('rateConfigurationStrings.diet.lengthAmountHeader'), $t(`enums.dietLengthTypes.${item.rateType}`)])"
                        />
                      </vee-field>
                    </td>
                  </tr>
                </tbody>
              </template>
            </v-table>
          </v-col>
          <v-col
            cols="12"
            lg="8"
            xl="6"
          >
            <v-card-subtitle>
              <h3 class="text-h3">
                {{ $t("rateConfigurationStrings.diet.staySubheader") }}
              </h3>
            </v-card-subtitle>
            <v-table>
              <template #default>
                <thead>
                  <tr>
                    <th
                      id="dietStayEnabledHeader"
                      class="text-left"
                    >
                      {{ $t('rateConfigurationStrings.diet.stayEnabledHeader') }}
                    </th>
                    <th
                      id="dietStayRateHeader"
                      class="text-left"
                    >
                      {{ $t('rateConfigurationStrings.diet.stayRateHeader') }}
                    </th>
                    <th
                      id="dietStayAmountHeader"
                      class="text-left"
                    >
                      {{ $t('rateConfigurationStrings.diet.stayAmountHeader') }}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="item in dietRates.dietStayRateTypes"
                    :key="item.rateType"
                  >
                    <td>
                      <vee-field
                        v-slot="{ componentField }"
                        v-model="item.enabled"
                        rules
                        :vid="getDietStayEnabledValidationId(item.rateType)"
                      >
                        <v-checkbox
                          v-bind="componentField"
                          color="primary"
                          :aria-label="$t('commonStrings.tableAriaLabel',[$t('rateConfigurationStrings.diet.stayEnabledHeader'), $t(`enums.dietStayTypes.${item.rateType}`)])"
                        />
                      </vee-field>
                    </td>
                    <td>{{ $t(`enums.dietStayTypes.${item.rateType}`) }}</td>
                    <td>
                      <vee-field
                        v-slot="{ componentField, errors }"
                        v-model="item.rate"
                        :rules="{required_if:getDietStayEnabledValidationId(item.rateType), integer: true, min_value: item.enabled ? 1 : 0}"
                        :name="$t('rateConfigurationStrings.diet.stayAmountHeader')"
                      >
                        <integer-text-field
                          v-bind="componentField"
                          name="rate"
                          :messages="verifyStayTypeTaxRatesWarnings(item)"
                          :error-messages="errors"
                          :disabled="!item.enabled"
                          :aria-label="$t('commonStrings.tableAriaLabel',[$t('rateConfigurationStrings.diet.stayAmountHeader'), $t(`enums.dietStayTypes.${item.rateType}`)])"
                        />
                      </vee-field>
                    </td>
                  </tr>
                </tbody>
              </template>
            </v-table>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>
  </v-skeleton-loader>
</template>

<script>
import { Field as VeeField } from "vee-validate";
import { toMoney } from "@/utils/numeric";
import IntegerTextField from "@/views/common/components/IntegerTextField";

export default {
  name: "DietSettings",
  components: {
    VeeField,
    IntegerTextField
  },
  props: {
    loading: {
      type: Boolean,
      required: true
    },
    dietRates: {
      type: Object,
      required: true
    },
    dietTaxRates: {
      type: Object,
      required: true
    }
  },
  data: () => ({}),
  computed: {
    isAnyDietEnabled() {
      return (
        this.dietRates.dietStayRateTypes.some(x => x.enabled) ||
        this.dietRates.dietLengthRateTypes.some(x => x.enabled)
      );
    }
  },
  watch: {},
  methods: {
    verifyLengthTypeTaxRatesWarnings(item) {
      return this.verifyTaxRatesWarnings(item, this.getLengthTypeMaxTaxRate, this.hasPositiveLengthTypeMaxTaxRate);
    },
    verifyStayTypeTaxRatesWarnings(item) {
      return this.verifyTaxRatesWarnings(item, this.getStayTypeMaxTaxRate, this.hasPositiveStayTypeMaxTaxRate);
    },
    verifyTaxRatesWarnings(item, maxRateMethod, positiveRateMethod){
      if (!item) {
        return [];
      }

      const maxTaxRate = maxRateMethod(item.rateType);

      if (
        positiveRateMethod(item.rateType) &&
        item.rate > maxTaxRate
      ) {
        return this.$t("warnings.exceedTaxRate", [toMoney(maxTaxRate)]);
      }

      // no warnings
      return [];
    },
    getDietLengthEnabledValidationId(rateType) {
      return `enabled_dietLengthType_${rateType}`;
    },
    getDietStayEnabledValidationId(rateType) {
      return `enabled_dietStayType_${rateType}`;
    },
    hasPositiveStayTypeMaxTaxRate(rateType) {
      const maxRate = this.getStayTypeMaxTaxRate(rateType);
      return maxRate && maxRate > 0;
    },
    getStayTypeMaxTaxRate(rateType) {
      if (this.dietTaxRates?.dietStayTypeMaxTaxRates) {
        const res = this.dietTaxRates.dietStayTypeMaxTaxRates.find(
          x => x.claimType === rateType
        );

        if(res) {
          return res.maxTaxRate;
        }
      }
      return null;
    },
    hasPositiveLengthTypeMaxTaxRate(rateType) {
      const maxRate = this.getLengthTypeMaxTaxRate(rateType);
      return maxRate && maxRate > 0;
    },
    getLengthTypeMaxTaxRate(rateType) {
      if (this.dietRates && this.dietTaxRates.dietLengthTypeMaxTaxRates) {
        const res = this.dietTaxRates.dietLengthTypeMaxTaxRates.find(
          x => x.claimType === rateType
        );

        if (res) {
          return res.maxTaxRate;
        }
      }
      return null;
    }
  }
};
</script>
